import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import Swal from "sweetalert2";

import { AiExtractionStateService } from "src/app/core/services/ai-extraction-state.service";
import { AiWalletStateService } from "src/app/core/services/ai-wallet-state.service";
import {
  ExtractionDocument,
  ExtractionPublicConfig,
  ExtractionResult,
  ExtractionRun,
  PropertyExtractionService,
} from "src/app/core/services/property-extraction.service";
import {
  PropertyDocumentType,
  PropertyDocumentsService,
} from "src/app/core/services/property-documents.service";

import { AiReviewConfirmEvent } from "../ai-extraction-review/ai-extraction-review.component";
import { AiFormPatch } from "../ai-extraction-patch.util";
import { AiCurrentValues, AiLookups } from "../ai-extraction.models";

type PanelStep = "loading" | "upload" | "triage" | "quote" | "processing" | "review" | "error";

interface PendingFile {
  key: string;
  file: File;
  name: string;
  sizeLabel: string;
}

interface QuoteRow {
  id: number;
  fileName: string;
  typeKey: string;
  categoryKey: string;
  categoryClass: string;
  pageCount: number;
  credits: number;
  readable: boolean;
  failed: boolean;
  note: string | null;
  fromProperty: boolean;
}

interface ProgressRow {
  id: number;
  fileName: string;
  statusKey: string;
  state: "waiting" | "working" | "done" | "failed";
}

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp";
const ACCEPTED_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp"];

/**
 * Painel da extração de documentos: carregar, orçamento, progresso e revisão.
 *
 * O run vive no servidor, por isso fechar o painel não perde nada: ao reabrir,
 * retoma-se onde ficou.
 */
@Component({
  selector: "app-ai-extraction-panel",
  templateUrl: "./ai-extraction-panel.component.html",
  styleUrls: ["./ai-extraction-panel.component.scss"],
})
export class AiExtractionPanelComponent implements OnInit, OnDestroy {
  @Input() propertyId: string | null = null;
  @Input() lookups: AiLookups | null = null;
  @Input() currentValues: AiCurrentValues | null = null;
  /** Documentos já guardados no imóvel a analisar (separador Documentos). */
  @Input() propertyFileIds: number[] = [];

  @Output() applied = new EventEmitter<AiFormPatch>();
  @Output() closed = new EventEmitter<void>();

  readonly accept = ACCEPT;

  step: PanelStep = "loading";
  config: ExtractionPublicConfig | null = null;
  run: ExtractionRun | null = null;
  result: ExtractionResult | null = null;
  documentTypes: PropertyDocumentType[] = [];

  pendingFiles: PendingFile[] = [];
  quoteRows: QuoteRow[] = [];
  progressRows: ProgressRow[] = [];
  rejectedNotes: string[] = [];
  dragging = false;
  busy = false;
  saving = false;
  errorMessage: string | null = null;
  balance: number | null = null;
  balanceAfter: number | null = null;
  insufficient = false;
  progressPct = 0;
  maxFiles = 32;

  /** Ficheiros carregados neste painel, para levar as fotografias à galeria. */
  private uploaded = new Map<string, File>();
  private pollSub?: Subscription;
  private subscriptions = new Subscription();

  constructor(
    private extraction: PropertyExtractionService,
    private propertyDocuments: PropertyDocumentsService,
    private wallet: AiWalletStateService,
    private state: AiExtractionStateService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.wallet.balance$.subscribe((balance: any) => {
        this.balance = typeof balance === "number" ? balance : null;
        this.updateBalance();
      }),
    );
    this.wallet.refresh();

    this.subscriptions.add(
      this.propertyDocuments.getActiveTypes().subscribe({
        next: (types) => (this.documentTypes = types ?? []),
        error: () => (this.documentTypes = []),
      }),
    );

    this.subscriptions.add(
      this.extraction.getPricing().subscribe({
        next: (config) => {
          this.config = config;
          this.maxFiles = config.limits.maxDocuments + config.limits.maxImages;
          this.start();
        },
        error: (error) => this.fail(error),
      }),
    );
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  @HostListener("document:keydown.escape")
  onEscape() {
    this.requestClose();
  }

  trackById(_: number, row: { id: number }) {
    return row.id;
  }

  trackByKey(_: number, row: PendingFile) {
    return row.key;
  }

  // ------------------------------------------------------------------ início

  private start() {
    if (this.propertyFileIds?.length) {
      this.createRun();
      return;
    }

    // Retoma um run em aberto deste utilizador para este imóvel.
    this.subscriptions.add(
      this.extraction.findOpenRun(this.propertyId).subscribe({
        next: (run) => {
          if (run) this.enter(run);
          else this.setStep("upload");
        },
        error: () => this.setStep("upload"),
      }),
    );
  }

  private setStep(step: PanelStep) {
    this.step = step;
    this.cdr.detectChanges();
  }

  private fail(error: any) {
    this.busy = false;
    this.errorMessage = this.messageOf(error);
    this.setStep("error");
  }

  private messageOf(error: any): string {
    const message = error?.error?.message ?? error?.message;
    if (Array.isArray(message)) return message.join(" ");
    return `${message ?? this.translate.instant("ai-extraction.errors.generic")}`;
  }

  /** Coloca o painel no passo certo para o estado do run. */
  private enter(run: ExtractionRun) {
    this.run = run;
    this.busy = false;
    this.rejectedNotes = (run.rejected ?? []).map(
      (item) =>
        `${item.fileName}: ${this.translate.instant(
          `ai-extraction.rejected.${item.reason}`,
        )}`,
    );

    switch (run.status) {
      case "TRIAGE":
        this.buildProgress(run);
        this.setStep("triage");
        this.poll(["TRIAGE"]);
        break;
      case "QUOTED":
        this.buildQuote(run);
        this.setStep("quote");
        break;
      case "PROCESSING":
        this.buildProgress(run);
        this.setStep("processing");
        this.poll(["PROCESSING"]);
        break;
      case "REVIEW":
        this.loadResult(run.id);
        break;
      case "FAILED":
        this.errorMessage =
          run.errorMessage ?? this.translate.instant("ai-extraction.errors.generic");
        this.setStep("error");
        break;
      default:
        this.run = null;
        this.setStep("upload");
    }
  }

  private poll(statuses: ExtractionRun["status"][]) {
    const runId = this.run?.id;
    if (!runId) return;
    this.pollSub?.unsubscribe();
    this.pollSub = this.extraction
      .waitWhile(runId, statuses, {
        onTick: (run) => {
          this.run = run;
          this.buildProgress(run);
          this.cdr.detectChanges();
        },
      })
      .subscribe({
        next: (run) => {
          if (run.status === "REVIEW") this.wallet.refresh();
          this.enter(run);
        },
        error: (error) => this.fail(error),
      });
  }

  // ----------------------------------------------------------------- ficheiros

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.dragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.dragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.dragging = false;
    this.addFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  onFilesChosen(event: Event) {
    const input = event.target as HTMLInputElement;
    this.addFiles(Array.from(input?.files ?? []));
    if (input) input.value = "";
  }

  private addFiles(files: File[]) {
    const maxBytes = (this.config?.limits.maxFileMb ?? 20) * 1024 * 1024;
    const notes: string[] = [];
    const next = this.pendingFiles.slice();
    const existing = this.run?.documents?.length ?? 0;

    for (const file of files) {
      const key = `${file.name}|${file.size}`;
      const known =
        ACCEPTED_TYPES.indexOf(file.type) >= 0 || /\.(pdf|jpe?g|png|webp)$/i.test(file.name);
      let reason: string | null = null;
      if (!known) reason = "UNSUPPORTED_FORMAT";
      else if (file.size > maxBytes) reason = "FILE_TOO_LARGE";
      else if (next.some((item) => item.key === key)) reason = "DUPLICATE";
      else if (existing + next.length >= this.maxFiles) reason = "TOO_MANY_FILES";

      if (reason) {
        notes.push(
          `${file.name}: ${this.translate.instant(`ai-extraction.rejected.${reason}`)}`,
        );
        continue;
      }
      next.push({ key, file, name: file.name, sizeLabel: this.sizeLabel(file.size) });
    }

    this.pendingFiles = next;
    this.rejectedNotes = notes;

    // No passo do orçamento os ficheiros novos seguem logo para análise.
    if (this.step === "quote" && this.pendingFiles.length) this.sendMoreFiles();
  }

  removePending(file: PendingFile) {
    this.pendingFiles = this.pendingFiles.filter((item) => item.key !== file.key);
  }

  private sizeLabel(bytes: number): string {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }

  private remember(files: PendingFile[]) {
    for (const item of files) this.uploaded.set(item.key, item.file);
  }

  createRun() {
    if (this.busy) return;
    const files = this.pendingFiles;
    if (!files.length && !this.propertyFileIds?.length) return;

    this.busy = true;
    this.remember(files);
    this.setStep("triage");

    this.subscriptions.add(
      this.extraction
        .createRun({
          files: files.map((item) => item.file),
          propertyFileIds: this.propertyFileIds ?? [],
          propertyId: this.propertyId,
          source: this.propertyFileIds?.length ? "DOCUMENTS_TAB" : "WIZARD",
        })
        .subscribe({
          next: (run) => {
            this.pendingFiles = [];
            this.enter(run);
          },
          error: (error) => {
            this.busy = false;
            this.errorMessage = this.messageOf(error);
            this.setStep(this.propertyFileIds?.length ? "error" : "upload");
          },
        }),
    );
  }

  private sendMoreFiles() {
    const runId = this.run?.id;
    if (!runId || this.busy) return;
    const files = this.pendingFiles;
    this.busy = true;
    this.remember(files);

    this.subscriptions.add(
      this.extraction
        .addDocuments(runId, { files: files.map((item) => item.file) })
        .subscribe({
          next: (run) => {
            this.pendingFiles = [];
            this.enter(run);
          },
          error: (error) => {
            this.busy = false;
            this.pendingFiles = [];
            this.errorMessage = this.messageOf(error);
            this.cdr.detectChanges();
          },
        }),
    );
  }

  removeDocument(row: QuoteRow) {
    const runId = this.run?.id;
    if (!runId || this.busy) return;
    this.busy = true;
    this.subscriptions.add(
      this.extraction.removeDocument(runId, row.id).subscribe({
        next: (run) => this.enter(run),
        error: (error) => {
          this.busy = false;
          this.errorMessage = this.messageOf(error);
          this.cdr.detectChanges();
        },
      }),
    );
  }

  // ---------------------------------------------------------------- orçamento

  private categoryOf(document: ExtractionDocument) {
    if (document.billingCategory === "IMAGE") {
      return { key: "ai-extraction.category.image", css: "badge-soft-info" };
    }
    if (document.billingCategory === "HANDWRITTEN") {
      return { key: "ai-extraction.category.handwritten", css: "badge-soft-warning" };
    }
    return { key: "ai-extraction.category.printed", css: "badge-soft-primary" };
  }

  private buildQuote(run: ExtractionRun) {
    this.quoteRows = (run.documents ?? []).map((document) => {
      const category = this.categoryOf(document);
      const failed = document.status === "FAILED";
      const unreadable = !document.readable || document.status === "UNREADABLE";
      return {
        id: document.id,
        fileName: document.fileName,
        typeKey: `ai-extraction.doc-types.${document.detectedType ?? "OUTRO"}`,
        categoryKey: category.key,
        categoryClass: category.css,
        pageCount: document.pageCount,
        credits: document.quotedCredits,
        readable: !unreadable && !failed,
        failed,
        note: unreadable || failed ? document.errorMessage : null,
        fromProperty: document.fromProperty,
      };
    });
    this.updateBalance();
  }

  private updateBalance() {
    const quoted = this.run?.quotedCredits ?? 0;
    if (this.balance === null) {
      this.balanceAfter = null;
      this.insufficient = false;
      return;
    }
    this.balanceAfter = this.balance - quoted;
    this.insufficient = this.balanceAfter < 0;
  }

  confirm() {
    const runId = this.run?.id;
    if (!runId || this.busy) return;
    this.busy = true;
    this.errorMessage = null;

    this.subscriptions.add(
      this.extraction.confirm(runId).subscribe({
        next: (run) => {
          this.wallet.refresh();
          this.enter(run);
        },
        error: (error) => {
          this.busy = false;
          this.errorMessage = this.messageOf(error);
          this.wallet.refresh();
          this.cdr.detectChanges();
        },
      }),
    );
  }

  // ---------------------------------------------------------------- progresso

  private buildProgress(run: ExtractionRun) {
    const triage = run.status === "TRIAGE";
    this.progressRows = (run.documents ?? []).map((document) => {
      let state: ProgressRow["state"] = "waiting";
      if (document.status === "PROCESSING") state = "working";
      else if (document.status === "FAILED" || document.status === "UNREADABLE") state = "failed";
      else if (document.status === "DONE") state = "done";
      else if (document.status === "TRIAGED") state = triage ? "done" : "waiting";
      else if (document.status === "PENDING" && triage) state = "working";
      return {
        id: document.id,
        fileName: document.fileName,
        statusKey: `ai-extraction.doc-status.${document.status}`,
        state,
      };
    });
    const total = this.progressRows.length || 1;
    const finished = this.progressRows.filter(
      (row) => row.state === "done" || row.state === "failed",
    ).length;
    this.progressPct = Math.round((finished / total) * 100);
  }

  cancelRun() {
    const runId = this.run?.id;
    if (!runId) return;
    Swal.fire({
      icon: "question",
      title: this.translate.instant("ai-extraction.cancel.title"),
      text: this.translate.instant("ai-extraction.cancel.text"),
      showCancelButton: true,
      confirmButtonColor: "rgb(54, 69, 116)",
      confirmButtonText: this.translate.instant("ai-extraction.cancel.confirm"),
      cancelButtonText: this.translate.instant("ai-extraction.cancel.keep"),
    }).then((answer) => {
      if (!answer.isConfirmed) return;
      this.pollSub?.unsubscribe();
      this.subscriptions.add(
        this.extraction.cancel(runId).subscribe({
          next: () => {
            this.wallet.refresh();
            this.run = null;
            this.result = null;
            this.setStep("upload");
          },
          error: (error) => this.fail(error),
        }),
      );
    });
  }

  // ------------------------------------------------------------------ revisão

  private loadResult(runId: number) {
    this.setStep("loading");
    this.subscriptions.add(
      this.extraction.getResult(runId).subscribe({
        next: (result) => {
          this.result = result;
          this.run = result.run;
          this.setStep("review");
        },
        error: (error) => this.fail(error),
      }),
    );
  }

  onRetryDocument(documentId: number) {
    const runId = this.run?.id;
    if (!runId) return;
    this.subscriptions.add(
      this.extraction.retryDocument(runId, documentId).subscribe({
        next: () => this.waitForRetry(runId, documentId),
        error: (error) => {
          this.errorMessage = this.messageOf(error);
          this.loadResult(runId);
        },
      }),
    );
  }

  /** O run fica em revisão: espera-se que o documento deixe de estar em curso. */
  private waitForRetry(runId: number, documentId: number, attempt = 0) {
    if (attempt > 80) {
      this.loadResult(runId);
      return;
    }
    setTimeout(() => {
      this.subscriptions.add(
        this.extraction.getRun(runId).subscribe({
          next: (run) => {
            const document = run.documents.find((item) => item.id === documentId);
            if (document?.status === "PROCESSING") {
              this.waitForRetry(runId, documentId, attempt + 1);
            } else {
              this.loadResult(runId);
            }
          },
          error: () => this.loadResult(runId),
        }),
      );
    }, 3000);
  }

  onConfirmed(event: AiReviewConfirmEvent) {
    const run = this.run;
    if (!run || this.saving) return;
    this.saving = true;

    this.subscriptions.add(
      this.extraction.saveDecisions(run.id, event.decisions).subscribe({
        next: () => {
          this.saving = false;
          const patch = event.patch;
          patch.photoFiles = this.photoFiles(run);
          this.state.setPending({
            runId: run.id,
            appliedFieldKeys: patch.appliedKeys,
          });
          this.applied.emit(patch);
        },
        error: (error) => {
          this.saving = false;
          this.errorMessage = this.messageOf(error);
          this.cdr.detectChanges();
        },
      }),
    );
  }

  /** Fotografias carregadas neste painel que o run reconheceu como tal. */
  private photoFiles(run: ExtractionRun): File[] {
    const files: File[] = [];
    for (const document of run.documents ?? []) {
      if (document.detectedType !== "FOTOGRAFIA" || document.fromProperty) continue;
      const file = this.uploaded.get(`${document.fileName}|${document.size}`);
      if (file) files.push(file);
    }
    return files;
  }

  // ------------------------------------------------------------------- fechar

  backToUpload() {
    this.errorMessage = null;
    this.run = null;
    this.result = null;
    this.setStep("upload");
  }

  requestClose() {
    if (this.saving) return;
    const hasWork =
      this.step === "quote" || this.step === "processing" || this.step === "review";
    if (!hasWork) {
      this.closed.emit();
      return;
    }

    Swal.fire({
      icon: "info",
      title: this.translate.instant("ai-extraction.close.title"),
      text: this.translate.instant(
        this.step === "review"
          ? "ai-extraction.close.text-review"
          : "ai-extraction.close.text",
      ),
      showCancelButton: true,
      confirmButtonColor: "rgb(54, 69, 116)",
      confirmButtonText: this.translate.instant("ai-extraction.close.confirm"),
      cancelButtonText: this.translate.instant("ai-extraction.close.stay"),
    }).then((answer) => {
      if (answer.isConfirmed) this.closed.emit();
    });
  }
}
